import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatService } from '../../services/chat.service';

/**
 * Secção 12: o leitor de uma mensagem de voz (como no WhatsApp): tocar / pausar,
 * barra de progresso que se pode arrastar, tempo e velocidade (1x, 1.5x, 2x).
 * O ficheiro é protegido (cookie): vem por HttpClient como blob e toca de um
 * object URL; `duration` vem dos params da mensagem porque o WebM que o browser
 * grava não declara a duração.
 */
@Component({
  selector: 'chat-audio-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="chat-audio" [class.chat-audio--out]="mine" [class.chat-audio--loading]="loading">
  <button type="button" class="chat-audio__btn" [attr.aria-label]="playing ? strings.pause : strings.play" [disabled]="!!error" (click)="toggle()">
    <span *ngIf="loading" class="chat-audio__spinner" aria-hidden="true"></span>
    <chat-icon *ngIf="!loading && !playing" name="play" [size]="16"></chat-icon>
    <chat-icon *ngIf="!loading && playing" name="pause" [size]="16"></chat-icon>
  </button>
  <div class="chat-audio__body">
    <input type="range" class="chat-audio__bar" min="0" [max]="total || 1" step="0.1" [value]="position" [attr.aria-label]="strings.voiceMessage"
      (input)="onSeek($event)" (change)="onSeek($event)">
    <div class="chat-audio__meta">
      <span>{{ error ? strings.voiceFailed : (playing || position > 0 ? format(position) : format(total)) }}</span>
      <button type="button" class="chat-audio__speed" (click)="cycleSpeed()" [attr.aria-label]="strings.playbackSpeed">{{ speed }}x</button>
    </div>
  </div>
  <audio #audio preload="metadata" (timeupdate)="onTime()" (ended)="onEnded()" (loadedmetadata)="onMeta()" (durationchange)="onMeta()"></audio>
</div>`,
  styles: [`
:host { display: block; min-width: 220px; max-width: 320px; }
.chat-audio { display: flex; align-items: center; gap: 10px; padding: 4px 2px; }
.chat-audio__btn { width: 36px; height: 36px; flex-shrink: 0; border: 0; border-radius: 50%; background: var(--chat-accent); color: var(--chat-on-accent); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.chat-audio--out .chat-audio__btn { background: var(--chat-on-accent); color: var(--chat-accent); }
.chat-audio__btn[disabled] { opacity: .5; cursor: default; }
.chat-audio__body { flex-grow: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.chat-audio__bar { width: 100%; height: 4px; margin: 6px 0; accent-color: var(--chat-accent); cursor: pointer; }
.chat-audio--out .chat-audio__bar { accent-color: var(--chat-on-accent); }
.chat-audio__meta { display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: inherit; opacity: .85; }
.chat-audio__speed { height: 18px; padding: 0 6px; border: 1px solid currentColor; border-radius: 9px; background: transparent; color: inherit; font: inherit; font-size: 10.5px; font-weight: 700; cursor: pointer; opacity: .9; }
.chat-audio__spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: currentColor; border-radius: 50%; animation: chat-audio-spin .8s linear infinite; }
@keyframes chat-audio-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .chat-audio__spinner { animation: none; } }
`]
})
export class ChatAudioPlayerComponent implements OnChanges, OnDestroy {
  /** URL protegido do anexo (ou `data:` da pré-visualização local). */
  @Input() src?: string;
  /** Duração em segundos (dos params da mensagem). */
  @Input() duration?: number;
  @Input() mine = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @ViewChild('audio', { static: true }) audioRef!: ElementRef<HTMLAudioElement>;

  playing = false;
  loading = false;
  error = false;
  position = 0;
  total = 0;
  speed = 1;
  private objectUrl: string | null = null;
  private loaded = false;
  private sub: Subscription | null = null;
  private readonly speeds = [1, 1.5, 2];

  constructor(private chatService: ChatService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['duration'] && this.duration && !this.loaded) {
      this.total = this.duration;
    }
    if (changes['src'] && !changes['src'].firstChange) {
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.reset();
  }

  toggle(): void {
    const audio = this.audioRef.nativeElement;
    if (this.playing) {
      audio.pause();
      this.playing = false;
      return;
    }
    if (!this.loaded) {
      this.load(() => this.start());
      return;
    }
    this.start();
  }

  private start(): void {
    const audio = this.audioRef.nativeElement;
    audio.playbackRate = this.speed;
    audio.play().then(() => {
      this.playing = true;
      this.cdr.markForCheck();
    }).catch(() => {
      this.error = true;
      this.cdr.markForCheck();
    });
  }

  /** O ficheiro é protegido: vem por HttpClient (cookie) e toca de um object URL. */
  private load(then: () => void): void {
    if (!this.src) {
      return;
    }
    if (this.src.startsWith('data:') || this.src.startsWith('blob:')) {
      this.audioRef.nativeElement.src = this.src;
      this.loaded = true;
      then();
      return;
    }
    this.loading = true;
    this.cdr.markForCheck();
    this.sub = this.chatService.getImage(this.src).subscribe({
      next: (blob: Blob) => {
        this.objectUrl = URL.createObjectURL(blob);
        this.audioRef.nativeElement.src = this.objectUrl;
        this.loaded = true;
        this.loading = false;
        this.cdr.markForCheck();
        then();
      },
      error: () => {
        this.loading = false;
        this.error = true;
        this.cdr.markForCheck();
      }
    });
  }

  onTime(): void {
    this.position = this.audioRef.nativeElement.currentTime || 0;
    this.cdr.markForCheck();
  }

  onMeta(): void {
    const d = this.audioRef.nativeElement.duration;
    if (isFinite(d) && d > 0) {
      this.total = d;
      this.cdr.markForCheck();
    }
  }

  onEnded(): void {
    this.playing = false;
    this.position = 0;
    this.cdr.markForCheck();
  }

  onSeek(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.position = value;
    if (this.loaded) {
      this.audioRef.nativeElement.currentTime = value;
    }
  }

  cycleSpeed(): void {
    const index = this.speeds.indexOf(this.speed);
    this.speed = this.speeds[(index + 1) % this.speeds.length];
    this.audioRef.nativeElement.playbackRate = this.speed;
  }

  format(seconds: number): string {
    const s = Math.max(0, Math.round(seconds || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  private reset(): void {
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
    }
    const audio = this.audioRef && this.audioRef.nativeElement;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.loaded = false;
    this.playing = false;
    this.position = 0;
    this.error = false;
    this.loading = false;
    this.total = this.duration || 0;
  }
}
